import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
    Box,
    Button,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import imageBackground from 'assets/images/backgrounds/bg-start-survey.jpeg';
import CircularProgress from '@mui/material/CircularProgress';
import {
    selectSurveyImage,
    selectWelcomeTextColor,
} from 'common/theme-selectors';
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft';
import BrandLogo from 'common/components/brand-logo';
import React, { useEffect, useState } from 'react';
import { createStyleSheet, handleError } from 'utils/helpers';
import ModalShareSurvey from '../components/modal-share-survey';
import ModalConfirm from 'common/components/modal-confirm';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { previewQuestionFormatter } from 'utils/preview_helpers';
import { fetchTargetedSurvey } from 'services/previewCampaign.service';
import { fetchSurveyGroup } from 'services/campaign.service';
import { useAppSelector, useAppDispatch } from 'common/hook';
import {
    setSurveyState,
    setListQuestion,
    captureTimeStamp,
} from 'slices/previewSurveySlice';
import { setAnonymousGroup } from 'slices/userSlice';
import { ICampaignQuestionDetail } from 'types/campaign';
import { toast } from 'react-toastify';

import { setCampaign } from 'slices/campaignsSlice';
import { campaignsResponseFormatter } from 'utils/campaign_helpers';
import { useTranslation } from 'react-i18next';
import { setGeoLocation, resetGeoLocation } from 'slices/geoLocationSlice';
import useGeolocation from 'common/hooks/use-geolocation';
import RequireLocationGate from 'common/components/require-location-gate';

interface IStartSurveyProps {
    setSurveyStep: (step: number) => void;
}
const StartTargetedSurvey: React.FC<IStartSurveyProps> = ({
    setSurveyStep,
}) => {
    const [openModalShareSurvey, setOpenModalShareSurvey] = useState(false);
    const [openModalCampaignStatus, setOpenModalCampaignStatus] =
        React.useState(false);
    const [openStaffIdModal, setOpenStaffIdModal] = useState(false);
    const [bgReady, setBgReady] = useState(false);
    const campaignId = useParams<{ id: string }>().id;
    const questions = useAppSelector((state) => state.previewSurvey.questions);
    const surveyState = useAppSelector(
        (state) => state.previewSurvey.surveyState
    );
    const surveyImage = useAppSelector(selectSurveyImage);
    const welcomeTextColor = useAppSelector(selectWelcomeTextColor);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const surveyCode = query.get('surveyCode');
    const uniqCode = query.get('code');
    const isSecretSurvey = query.get('isSecretSurvey');
    const queryStaffId = query.get('staffId') || query.get('staff_id') || '';
    const [staffId, setStaffId] = useState(queryStaffId);
    const {
        status: geoStatus,
        coords: geoCoords,
        error: geoError,
        requestLocation,
    } = useGeolocation();

    // Chỉ bắt bật vị trí khi campaign bật cờ is_required_location VÀ khảo sát đã
    // sẵn sàng trả lời (đã qua cổng nhập mã, đã tải xong câu hỏi).
    const surveyReady = !!surveyState.answerable && questions.length > 0;
    const requireLocation = surveyReady && !!surveyState.is_required_location;
    const locationReady = !requireLocation || geoStatus === 'granted';

    useEffect(() => {
        dispatch(resetGeoLocation());
    }, [dispatch]);

    useEffect(() => {
        if (requireLocation) {
            requestLocation();
        }
    }, [requireLocation, requestLocation]);

    useEffect(() => {
        if (geoCoords) {
            dispatch(setGeoLocation(geoCoords));
        }
    }, [geoCoords, dispatch]);

    // Chưa cấp quyền vị trí thì không cho vào phần trả lời, kể cả bằng phím Enter.
    const handleStartSurvey = () => {
        if (!locationReady) return;
        dispatch(captureTimeStamp());
        setSurveyStep(1);
    };

    // Màn nhập mã bảo mật chưa gọi API nào -> tải group riêng để có theme sớm.
    // bg/welcome đọc qua selector nên chỉ cần dispatch group vào store.
    // Chỉ bật bgReady sau khi đã có group + ảnh nền để paint đầu tiên là brand (tránh nháy).
    const loadThemeForGate = async () => {
        try {
            if (campaignId) {
                const res = await fetchSurveyGroup(campaignId);
                const group = res.success ? res.data.groups?.[0] : undefined;
                if (group) {
                    dispatch(setAnonymousGroup(group));
                    const apiImage = group.survey_image;
                    if (apiImage) {
                        const img = new Image();
                        img.onload = () => setBgReady(true);
                        img.onerror = () => setBgReady(true);
                        img.src = apiImage;
                        return;
                    }
                }
            }
        } catch {
            // không có theme -> giữ fallback mặc định
        }
        setBgReady(true);
    };

    const fetchQuestions = async () => {
        if (questions.length > 0) return;
        try {
            const res = await fetchTargetedSurvey(
                campaignId as string,
                staffId as string
            );
            if (res.success) {
                const group = res.data.groups?.[0];
                if (group) {
                    dispatch(setAnonymousGroup(group));
                }
                const formattedData = res.data.questions.map(
                    (item: ICampaignQuestionDetail) =>
                        previewQuestionFormatter(item)
                );
                dispatch(setSurveyState(res.data.survey.attributes));
                dispatch(setSurveyState({ answerable: true }));
                dispatch(setListQuestion(formattedData));
                dispatch(
                    setCampaign(campaignsResponseFormatter(res.data.survey, t))
                );
                const queryParams = new URLSearchParams();
                queryParams.set('code', uniqCode || '');
                queryParams.set('staff_id', staffId || queryStaffId || '');
                navigate(
                    `/survey/invite/${campaignId}?${queryParams.toString()}`,
                    { replace: true }
                );
                setOpenStaffIdModal(false);
                setStaffId('');
            } else {
                if (res.status_code === 2100) {
                    dispatch(setSurveyState(res.data.survey.attributes));
                    setOpenModalCampaignStatus(true);
                } else {
                    toast.error(res.message);
                }
                setBgReady(true);
            }
        } catch (error) {
            handleError(error);
            setBgReady(true);
        }
    };

    const handleStaffIdSubmit = async () => {
        if (surveyCode && surveyCode.length > 0) {
            fetchQuestions();
        }
    };

    useEffect(() => {
        if (!!surveyCode && surveyCode.length > 0) {
            if (
                ((!!uniqCode && uniqCode.length > 0) ||
                    isSecretSurvey === 'true') &&
                !queryStaffId
            ) {
                setOpenStaffIdModal(true);
                loadThemeForGate();
            } else {
                fetchQuestions();
            }
        } else {
            fetchQuestions();
        }
    }, [
        campaignId,
        dispatch,
        surveyCode,
        uniqCode,
        isSecretSurvey,
        queryStaffId,
    ]);

    useEffect(() => {
        if (questions.length > 0 && !bgReady) {
            setBgReady(true);
        }
    }, [questions, bgReady]);

    // eslint-disable-next-line
    const handleEnterKey = async (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleStartSurvey();
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleEnterKey);
        return () => {
            window.removeEventListener('keydown', handleEnterKey);
        };
    }, [handleEnterKey]);

    if (!bgReady) {
        return (
            <Box
                sx={{
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <RequireLocationGate
            status={geoStatus}
            error={geoError}
            onRequest={requestLocation}
            enabled={requireLocation}
        >
            <Box
                sx={{
                    ...styles.startSurveyLayout,
                    backgroundImage: `url(${surveyImage ?? imageBackground})`,
                }}
            >
                <Grid
                    container
                    spacing={2}
                    sx={{
                        marginBottom: ['3rem', '4rem', '6rem', '8rem'],
                        alignItems: ['flex-end'],
                    }}
                >
                    <Grid item xs={12} md={4} sm={6}>
                        <BrandLogo width={130} />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={8}
                        sx={{
                            display: ['none', 'flex', 'flex', 'flex'],
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    ></Grid>
                </Grid>

                <Grid
                    container
                    spacing={2}
                    sx={{
                        marginBottom: ['3rem'],
                    }}
                >
                    <Grid item xs={12} md={7} lg={5} xl={5}>
                        <Typography
                            variant="h4"
                            align="left"
                            sx={{
                                fontSize: [
                                    '2rem',
                                    '2rem',
                                    '3rem',
                                    '3.5rem',
                                    '3.5rem',
                                ],
                                fontWeight: '900',
                                lineHeight: [
                                    '2.5rem',
                                    '2.5rem',
                                    '3.5rem',
                                    '4rem',
                                    '4rem',
                                ],
                                color: welcomeTextColor,
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1rem',
                                    '2rem',
                                    '2rem',
                                ],
                            }}
                        >
                            {surveyState.title}
                        </Typography>
                        <Typography
                            variant="h5"
                            align="left"
                            sx={{
                                fontSize: [
                                    '1.5rem',
                                    '1.5rem',
                                    '2rem',
                                    '2.2rem',
                                    '2.2rem',
                                ],
                                fontWeight: '700',
                                lineHeight: [
                                    '2rem',
                                    '2rem',
                                    '2.5rem',
                                    '3rem',
                                    '3rem',
                                ],
                                color: welcomeTextColor,
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1.5rem',
                                    '1.5rem',
                                ],
                            }}
                        >
                            Chúc mừng bạn đến với khảo sát
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: welcomeTextColor,
                                fontWeight: '500',
                                fontSize: ['1rem', '1rem', '1.5rem', '1.5rem'],
                                lineHeight: ['1.3rem', '1.5rem', '1.8rem'],
                            }}
                        >
                            Cảm ơn bạn đã đồng ý tham gia. Sẽ chỉ mất vài phút
                            để hoàn thành khảo sát. <br /> Tất cả các câu trả
                            lời của bạn là riêng tư và bí mật.
                        </Typography>
                    </Grid>
                </Grid>
                {surveyState.answerable && questions.length > 0 && (
                    <Grid container spacing={2} marginBottom={'20px'}>
                        <Grid
                            item
                            xs={12}
                            md={12}
                            display={'flex'}
                            alignItems={'center'}
                            gap={2}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                sx={styles.btnStartSurvey}
                                disabled={!locationReady}
                                onClick={handleStartSurvey}
                            >
                                Bắt đầu khảo sát
                            </Button>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: welcomeTextColor,
                                    fontSize: [
                                        '1rem',
                                        '1rem',
                                        '1.5rem',
                                        '1.5rem',
                                    ],
                                    display: [
                                        'none',
                                        'none',
                                        'none',
                                        'flex',
                                        'flex',
                                    ],
                                    alignItems: 'baseline',
                                    gap: 1,
                                }}
                            >
                                Ấn
                                <span
                                    style={{
                                        fontWeight: '700',
                                    }}
                                >
                                    Enter
                                </span>
                                <SubdirectoryArrowLeftIcon
                                    sx={{ fontSize: 32 }}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        md={12}
                        display={'flex'}
                        alignItems={'center'}
                        gap={2}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: welcomeTextColor,
                                fontWeight: '400',
                                fontStyle: 'italic',
                                fontSize: ['0.8rem', '1rem', '1rem', '1rem'],
                                lineHeight: ['1.5rem', '1.5rem', '2rem'],
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <AccessTimeIcon
                                sx={{
                                    fontSize: ['1rem', '1.2rem'],
                                }}
                            />
                            Sẽ mất 5 phút để hoàn thành khảo sát này
                        </Typography>
                    </Grid>
                </Grid>
                {openModalShareSurvey && (
                    <ModalShareSurvey
                        open={openModalShareSurvey}
                        onClose={() => setOpenModalShareSurvey(false)}
                    />
                )}
                {openModalCampaignStatus && (
                    <ModalConfirm
                        open={true}
                        onClose={() => setOpenModalCampaignStatus(false)}
                        title="Thông báo"
                        content={
                            <Box
                                sx={{
                                    fontSize: '24px',
                                }}
                            >
                                Chiến dịch khảo sát
                                <b> {`"${surveyState.title}"`}</b> đã kết thúc.
                                <br />
                                Vui lòng kiểm tra lại sau hoặc tham các chiến
                                dịch khác
                            </Box>
                        }
                    />
                )}
                <Dialog open={openStaffIdModal}>
                    <DialogTitle>Nhập mã trả lời</DialogTitle>
                    <DialogContent sx={{ minWidth: '300px' }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Mã"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={staffId}
                            onChange={(e) => setStaffId(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleStaffIdSubmit();
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenStaffIdModal(false)}>
                            Hủy
                        </Button>
                        <Button
                            onClick={handleStaffIdSubmit}
                            variant="contained"
                        >
                            Xác nhận
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </RequireLocationGate>
    );
};
const styles = createStyleSheet({
    startSurveyLayout: {
        width: '100vw',
        minHeight: '100vh',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: ['2rem', '2rem', '4rem', '4rem'],
    },
    btnStartSurvey: {
        padding: [
            '1rem 3rem',
            '1rem 3rem',
            '1.2rem 3.2rem',
            '1.5rem 3.5rem',
            '1.5rem 1.5rem',
        ],
        minWidth: ['10rem', '10rem', '15rem', '15rem'],
        height: ['2.5rem', '2.7rem', '3rem', '4rem', '4rem'],
        borderRadius: '50px',
        fontSize: ['1rem', '1rem', '1.2rem', '1.5rem', '1.5rem'],
        fontWeight: '700',
        lineHeight: ['1.5rem', '1.5rem', '2rem'],
    },
    btnShare: {
        minWidth: ['5rem', '7rem', '10rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});
export default StartTargetedSurvey;
